/**
 * ========================
 * TASKS DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the Tasks domain.
 * Sections:
 *  1. Overview - Dashboard (all users)
 *  2. My Work - Assigned Tasks, Board, My Tasks, Calendar, Prioritiser (all users)
 *  3. Create - Create Task, Quick Task (all users with Task.Create)
 *  4. Alerts - Notifications (all users)
 *  5. Administration - All Tasks, Projects, Reports, Lookups, Settings (admin)
 */

import {
  IconClockPlay,
  IconLayoutDashboard,
  IconListCheck,
  IconLayoutBoard,
  IconEye,
  IconCalendar,
  IconBrain,
  IconPlus,
  IconReportAnalytics,
  IconSettings,
  IconBell,
  IconClipboardList,
  IconFolderOpen,
  IconTableOptions,
  IconChecklist,
  IconNote,
  IconMapPin,
  IconBuilding,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection } from '../../layouts/menu/types';
import { createVisibilityCheck, anyVisibilityCheck } from './BaseMenuProvider';
import { useTaskBadgesStore } from '@/domain/badges/taskBadgesStore';
import { useTaskStore } from '../../stores/taskStore';

class TasksMenuProvider implements DomainMenuProvider {
  domainId = 'tasks';

  getMenuConfig() {
    // Users with 'Tasks Access' role OR Task.View permission can see task pages
    const allUsers = anyVisibilityCheck({ roles: ['Tasks Access'] }, { permissions: ['Task.View'] });
    const creators = anyVisibilityCheck({ roles: ['Tasks Access'] }, { permissions: ['Task.Create'] });
    // Admin: user has Admin role OR Task.Admin permission
    const admins = anyVisibilityCheck({ roles: ['Admin'] }, { permissions: ['Task.Admin'] });

    const sections: MenuSection[] = [
      // Overview
      {
        id: 'tasks-overview',
        label: 'Overview',
        items: [
          {
            id: 'tasks-dashboard',
            label: 'Dashboard',
            path: '/app/tasks',
            icon: <IconLayoutDashboard size={16} />,
            visible: allUsers,
          },
        ],
      },

      // My Work
      {
        id: 'tasks-my-work',
        label: 'My Work',
        items: [
          {
            id: 'tasks-upcoming',
            label: 'Upcoming',
            path: '/app/tasks/upcoming',
            icon: <IconClockPlay size={16} />,
            visible: allUsers,
            countSelector: () => useTaskBadgesStore.getState().upcoming || null,
            badgeColor: 'indigo',
          },
          {
            id: 'tasks-assigned',
            label: 'Assigned Tasks',
            path: '/app/tasks/assigned-tasks',
            icon: <IconListCheck size={16} />,
            visible: allUsers,
            countSelector: () => useTaskBadgesStore.getState().unseenAssigned || null,
            badgeColor: 'orange',
          },
          {
            id: 'tasks-looped-in',
            label: 'Looped In Tasks',
            path: '/app/tasks/looped-in-tasks',
            icon: <IconEye size={16} />,
            visible: allUsers,
            // Baseline-only - refreshed on mount / reconnect.
            countSelector: () => useTaskBadgesStore.getState().loopedInActive || null,
            badgeColor: 'blue',
          },
          {
            id: 'tasks-board',
            label: 'Board',
            path: '/app/tasks/board',
            icon: <IconLayoutBoard size={16} />,
            visible: allUsers,
          },
          {
            id: 'tasks-my-tasks',
            label: 'My Tasks',
            path: '/app/tasks/my-tasks',
            icon: <IconClipboardList size={16} />,
            visible: allUsers,
            countSelector: () => useTaskBadgesStore.getState().overdueAssigned || null,
            badgeColor: 'red',
          },
          {
            id: 'tasks-calendar',
            label: 'Calendar',
            path: '/app/tasks/calendar',
            icon: <IconCalendar size={16} />,
            visible: allUsers,
          },
          {
            id: 'tasks-prioritiser',
            label: 'Task Prioritiser',
            path: '/app/tasks/ai-prioritizer',
            icon: <IconBrain size={16} />,
            visible: allUsers,
          },
        ],
      },

      // Create
      {
        id: 'tasks-create',
        label: 'Create',
        visible: creators,
        items: [
          {
            id: 'tasks-new-task',
            label: 'New Task',
            path: '/app/tasks/create',
            icon: <IconPlus size={16} />,
            visible: creators,
          },
          {
            id: 'tasks-quick-task',
            label: 'Quick Task',
            icon: <IconNote size={16} />,
            visible: creators,
            onClick: () => useTaskStore.getState().setQuickTaskModalOpen(true),
          },
        ],
      },

      // Alerts - hidden until the Notifications page is finished. The route
      // is still wired in TaskManagement/index.tsx so deep-links keep working.
      {
        id: 'tasks-alerts',
        label: 'Alerts',
        visible: () => false,
        items: [
          {
            id: 'tasks-notifications',
            label: 'Notifications',
            path: '/app/tasks/notifications',
            icon: <IconBell size={16} />,
            visible: () => false,
          },
        ],
      },

      // Administration
      {
        id: 'tasks-admin',
        label: 'Administration',
        visible: admins,
        items: [
          {
            id: 'tasks-admin-all',
            label: 'All Tasks',
            path: '/app/tasks/admin/all-tasks',
            icon: <IconChecklist size={16} />,
            visible: admins,
          },
          {
            id: 'tasks-admin-projects',
            label: 'Projects',
            path: '/app/tasks/admin/projects',
            icon: <IconFolderOpen size={16} />,
            visible: admins,
          },
          {
            id: 'tasks-admin-campuses',
            label: 'Campuses',
            path: '/app/tasks/admin/locations',
            icon: <IconMapPin size={16} />,
            visible: admins,
          },
          {
            id: 'tasks-admin-departments',
            label: 'Departments',
            path: '/app/tasks/admin/departments',
            icon: <IconBuilding size={16} />,
            visible: admins,
          },
          {
            id: 'tasks-admin-reports',
            label: 'Reports',
            path: '/app/tasks/admin/reports',
            icon: <IconReportAnalytics size={16} />,
            visible: admins,
          },
          {
            id: 'tasks-admin-ai-insights',
            label: 'AI Insights',
            path: '/app/tasks/admin/ai-insights',
            icon: <IconBrain size={16} />,
            visible: admins,
          },
          {
            id: 'tasks-admin-lookups',
            label: 'Lookups',
            path: '/app/tasks/admin/lookups',
            icon: <IconTableOptions size={16} />,
            visible: admins,
          },
          {
            id: 'tasks-admin-settings',
            label: 'Settings',
            path: '/app/tasks/admin/settings',
            icon: <IconSettings size={16} />,
            visible: admins,
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '3.1.0',
        lastUpdated: Date.now(),
        description: 'Task management menu - sectioned layout',
      },
    };
  }
}

export const tasksMenuProvider = new TasksMenuProvider();
